import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 
import { useFontSize } from './FontSizeContext';

import { Plus, Search, User as UserIcon, Shield, ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const fontContext = useFontSize();

  const fontSize = fontContext?.fontSize || 13;
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const itemsPerPage = 15;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/users`, {
        params: { page: currentPage, limit: itemsPerPage, q: searchTerm },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(res.data.data);
      setTotalItems(res.data.total);
    } catch (err) {
      toast.error(t('userList.errLoadUsers')); // 🎯
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[11px] font-bold uppercase tracking-tighter ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }}>
      {/* Toolbar */}
      <div className="bg-surface-card p-3 border-b border-line shadow-sm flex justify-between items-center">
        <div className={`flex items-center gap-6 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <h2 className="text-primary text-lg italic flex items-center gap-2">
            <UserIcon size={20} /> {t('userList.title')} {/* 🎯 */}
          </h2>
          <div className="relative">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-2.5 text-ink-secondary`} size={14} />
            <input
              className={`py-2 border border-line w-80 outline-none focus:border-primary rounded-sm shadow-inner ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'}`}
              placeholder={t('userList.plcSearch')} // 🎯
              value={searchTerm}
              onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
            />
          </div>
        </div>
        <button onClick={() => navigate('/users/new')} className={`bg-primary text-white px-6 py-2 rounded-sm flex items-center gap-2 hover:bg-primary-dark transition-all shadow-lg ${isRTL ? 'ml-4' : 'mr-4'}`}>
          <Plus size={16} /> {t('userList.btnNewUser')} {/* 🎯 */}
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-surface-card border-2 border-line rounded-sm shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-primary-dark text-white sticky top-0 z-10">
              <tr>
                <th className={`p-3 border-white/10 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{t('userList.colUserId')}</th>
                <th className={`p-3 border-white/10 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{t('userList.colFullName')}</th>
                <th className={`p-3 border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('userList.colRole')}</th>
                <th className={`p-3 border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('userList.colSite')}</th>
                <th className={`p-3 border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('userList.colStatus')}</th>
                <th className="p-3 text-center">{t('userList.colEmail')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {loading ? (
                <tr><td colSpan="6" className="p-10 text-center animate-pulse">{t('userList.msgSyncing')}</td></tr>
              ) : users.map((u) => (
                <tr key={u.user_id} className="hover:bg-primary-soft cursor-pointer" onClick={() => navigate(`/users/${u.user_id}`)}>
                  <td className={`p-3 font-black text-primary ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{u.username}</td>
                  <td className={`p-3 text-ink ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{u.full_name}</td>
                  <td className={`p-3 text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                    <span className="flex items-center justify-center gap-1 text-status-warning">
                      <Shield size={12} /> {u.role_name || t('userList.lblNoRole')}
                    </span>
                  </td>
                  <td className={`p-3 text-center text-ink-secondary italic ${isRTL ? 'border-l' : 'border-r'}`}>{u.site_id || t('userList.lblAll')}</td>
                  <td className={`p-3 text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                    {u.is_active ?
                      <CheckCircle size={14} className="text-status-success mx-auto" /> :
                      <XCircle size={14} className="text-status-danger mx-auto" />
                    }
                  </td>
                  <td className="p-3 text-center text-ink-secondary lowercase italic">{u.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner">
        <span className="text-sky">{t('userList.lblTotalUsers')} {totalItems}</span>
        <div className="flex items-center gap-2">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="p-1 border rounded disabled:opacity-20">
              <ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <span>{t('userList.lblPage')} {currentPage}</span>
          <button disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)} className="p-1 border rounded disabled:opacity-20">
              <ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserList;